import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { MKT_POSTS_DESIGN as d } from './MktPosts.design';
import { validatePost, hashtagsToText, resolveValidationMessage } from './constants';

function truncate(text, max, moreLabel) {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max)}... ${moreLabel}` : text;
}

function firstMedia(media) {
    return media && media.length > 0 ? media[0] : null;
}

function MediaFrame({ media, aspect = 'aspect-square', showCount = true }) {
    const item = firstMedia(media);
    if (!item) {
        return (
            <div className={`${aspect} bg-gray-100 flex items-center justify-center`}>
                <Iconify icon="eva:image-outline" width={32} className="text-gray-300" />
            </div>
        );
    }
    return (
        <div className={`relative ${aspect} bg-black`}>
            {item.type === 'video' ? (
                <>
                    {item.thumbnail || item.url ? (
                        <img src={item.thumbnail || item.url} alt="" className="w-full h-full object-cover" />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center">
                            <Iconify icon="eva:play-circle-fill" width={28} className="text-white" />
                        </div>
                    </div>
                </>
            ) : (
                <img src={item.url} alt="" className="w-full h-full object-cover" />
            )}
            {showCount && media.length > 1 && (
                <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                    1/{media.length}
                </span>
            )}
        </div>
    );
}

function InstagramFeedPreview({ text, hashtags, media, account, t }) {
    const caption = [text, hashtagsToText(hashtags)].filter(Boolean).join(' ');
    const username = account?.account_info?.username || account?.account_name || t('posts.preview.defaultAccount');

    return (
        <div className={d.previewCard.phoneFrame}>
            <div className={d.previewCard.notch} />
            <div className={d.previewCard.phoneScreen}>
                <div className="flex items-center justify-between px-3 py-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                        {account?.profile_picture ? (
                            <img src={account.profile_picture} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                        ) : (
                            <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-violet-500 flex-shrink-0" />
                        )}
                        <span className="text-xs font-semibold text-gray-900 truncate">{username}</span>
                    </div>
                    <Iconify icon="eva:more-horizontal-outline" width={18} className="text-gray-700" />
                </div>

                <MediaFrame media={media} aspect="aspect-square" />

                <div className="px-3 pt-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Iconify icon="eva:heart-outline" width={22} className="text-gray-800" />
                        <Iconify icon="eva:message-circle-outline" width={22} className="text-gray-800" />
                        <Iconify icon="eva:paper-plane-outline" width={20} className="text-gray-800" />
                    </div>
                    <Iconify icon="eva:bookmark-outline" width={20} className="text-gray-800" />
                </div>

                <div className="px-3 pt-1.5 pb-3">
                    <p className="text-xs font-semibold text-gray-900">{t('posts.preview.beFirstToLike')}</p>
                    {caption ? (
                        <p className="text-xs text-gray-800 mt-1">
                            <span className="font-semibold">{username}</span> {truncate(caption, 125, t('posts.preview.more'))}
                        </p>
                    ) : (
                        <p className="text-xs text-gray-300 mt-1 italic">{t('posts.preview.noCaption')}</p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1 uppercase">{t('posts.preview.secondsAgo')}</p>
                </div>
            </div>
        </div>
    );
}

function InstagramStoryPreview({ media, account, t }) {
    const username = account?.account_info?.username || account?.account_name || t('posts.preview.defaultAccount');
    return (
        <div className={d.previewCard.phoneFrame}>
            <div className={d.previewCard.notch} />
            <div className="bg-black rounded-[2rem] overflow-hidden relative aspect-[9/16]">
                <div className="absolute top-2 left-2 right-2 flex gap-1 z-10">
                    <div className="h-0.5 flex-1 bg-white/40 rounded-full overflow-hidden"><div className="h-full w-1/3 bg-white" /></div>
                </div>
                <div className="absolute top-5 left-3 right-3 flex items-center gap-2 z-10">
                    {account?.profile_picture ? (
                        <img src={account.profile_picture} alt="" className="w-7 h-7 rounded-full object-cover ring-2 ring-white/60" />
                    ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-violet-500 ring-2 ring-white/60" />
                    )}
                    <span className="text-xs font-semibold text-white drop-shadow">{username}</span>
                </div>
                <div className="absolute inset-0">
                    <MediaFrame media={media} aspect="h-full" showCount={false} />
                </div>
                <p className="absolute bottom-3 left-3 right-3 text-[10px] text-white/70 text-center">
                    {t('posts.preview.storyNoCaptionApi')}
                </p>
            </div>
        </div>
    );
}

function InstagramReelPreview({ text, hashtags, media, account, t }) {
    const caption = [text, hashtagsToText(hashtags)].filter(Boolean).join(' ');
    const username = account?.account_info?.username || account?.account_name || t('posts.preview.defaultAccount');
    return (
        <div className={d.previewCard.phoneFrame}>
            <div className={d.previewCard.notch} />
            <div className="bg-black rounded-[2rem] overflow-hidden relative aspect-[9/16]">
                <MediaFrame media={media} aspect="h-full" showCount={false} />
                <div className="absolute right-2 bottom-16 flex flex-col items-center gap-4 z-10">
                    <Iconify icon="eva:heart-outline" width={24} className="text-white" />
                    <Iconify icon="eva:message-circle-outline" width={24} className="text-white" />
                    <Iconify icon="eva:paper-plane-outline" width={22} className="text-white" />
                </div>
                <div className="absolute left-3 right-14 bottom-4 z-10">
                    <p className="text-xs font-semibold text-white drop-shadow">{username}</p>
                    {caption && <p className="text-[11px] text-white/90 mt-1 drop-shadow">{truncate(caption, 90, t('posts.preview.more'))}</p>}
                </div>
            </div>
        </div>
    );
}

function FacebookPreview({ text, media, link, account, scheduledDate, t }) {
    const pageName = account?.account_name || t('posts.preview.defaultPage');
    const when = scheduledDate ? t('posts.preview.scheduled') : t('posts.preview.now');

    return (
        <div className={d.previewCard.phoneFrame}>
            <div className={d.previewCard.notch} />
            <div className={d.previewCard.phoneScreen}>
                <div className="flex items-center gap-2 px-3 py-2.5">
                    {account?.profile_picture ? (
                        <img src={account.profile_picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Iconify icon="mdi:facebook" width={18} className="text-blue-600" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-gray-900 truncate">{pageName}</p>
                        <p className="text-[10px] text-gray-500 flex items-center gap-1">{when} · <Iconify icon="eva:globe-2-outline" width={10} /></p>
                    </div>
                </div>

                {text && (
                    <p className="px-3 pb-2 text-xs text-gray-800 whitespace-pre-wrap">
                        {truncate(text, 250, t('posts.preview.more'))}
                    </p>
                )}

                {media && media.length > 0 ? (
                    <MediaFrame media={media} aspect="aspect-[4/3]" />
                ) : link ? (
                    <div className="mx-3 mb-2 border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-100 aspect-[2/1] flex items-center justify-center">
                            <Iconify icon="eva:link-2-outline" width={24} className="text-gray-400" />
                        </div>
                        <div className="px-2.5 py-2">
                            <p className="text-[10px] text-gray-400 uppercase truncate">{link}</p>
                        </div>
                    </div>
                ) : null}

                <div className="flex items-center justify-around px-3 py-2 border-t border-gray-100 mt-1">
                    <span className="text-[11px] text-gray-500 flex items-center gap-1"><Iconify icon="eva:thumbs-up-outline" width={14} /> {t('posts.preview.like')}</span>
                    <span className="text-[11px] text-gray-500 flex items-center gap-1"><Iconify icon="eva:message-square-outline" width={14} /> {t('posts.preview.comment')}</span>
                    <span className="text-[11px] text-gray-500 flex items-center gap-1"><Iconify icon="eva:share-outline" width={14} /> {t('posts.preview.share')}</span>
                </div>
            </div>
        </div>
    );
}

function ValidationList({ platforms, postType, text, media, hashtags, t }) {
    const { errors, warnings } = validatePost({ platforms, postType, text, media, hashtags }, t);
    if (errors.length === 0 && warnings.length === 0) return null;

    return (
        <div className={d.validationList.container}>
            {errors.map((item) => (
                <div key={JSON.stringify(item)} className={d.validationList.error}>
                    <Iconify icon="eva:alert-circle-outline" width={14} className="flex-shrink-0 mt-0.5" />
                    <span>{resolveValidationMessage(item, t)}</span>
                </div>
            ))}
            {warnings.map((item) => (
                <div key={JSON.stringify(item)} className={d.validationList.warning}>
                    <Iconify icon="eva:alert-triangle-outline" width={14} className="flex-shrink-0 mt-0.5" />
                    <span>{resolveValidationMessage(item, t)}</span>
                </div>
            ))}
        </div>
    );
}

export default function MktPostPreview({ platforms, postType, text, hashtags, media, link, accounts, scheduledDate }) {
    const { t } = useTranslation('marketing');
    const available = (platforms && platforms.length > 0) ? platforms : ['instagram'];
    const [activeTab, setActiveTab] = useState(available[0]);

    useEffect(() => {
        if (!available.includes(activeTab)) setActiveTab(available[0]);
    }, [available.join(','), activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    const account = accounts?.[activeTab];

    return (
        <div>
            {available.length > 1 && (
                <div className={d.previewCard.tabs}>
                    {available.map((p) => (
                        <button
                            key={p}
                            type="button"
                            className={activeTab === p ? d.previewCard.tabActive : d.previewCard.tab}
                            onClick={() => setActiveTab(p)}
                        >
                            {t(`posts.platforms.${p}`)}
                        </button>
                    ))}
                </div>
            )}

            {activeTab === 'instagram' && postType === 'story' && (
                <InstagramStoryPreview media={media} account={account} t={t} />
            )}
            {activeTab === 'instagram' && postType === 'reel' && (
                <InstagramReelPreview text={text} hashtags={hashtags} media={media} account={account} t={t} />
            )}
            {activeTab === 'instagram' && (postType === 'feed' || postType === 'carousel') && (
                <InstagramFeedPreview text={text} hashtags={hashtags} media={media} account={account} t={t} />
            )}
            {activeTab === 'facebook' && (
                <FacebookPreview text={text} media={media} link={link} account={account} scheduledDate={scheduledDate} t={t} />
            )}

            <ValidationList platforms={platforms} postType={postType} text={text} media={media} hashtags={hashtags} t={t} />
        </div>
    );
}
